import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  Stack,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import LinearProgress from "@mui/material/LinearProgress";
import { Link } from "react-router-dom";
import Layout from "../../components/Layout";
import { getTransactions, refundOrderTransaction } from "../../api";
import TrackEvents from "../../api/amplitudeEvents";

function EnhancedTableHead() {
  const headerStyle = { whiteSpace: "nowrap", padding: "6px 8px" };
  return (
    <TableHead>
      <TableRow>
        <TableCell sx={headerStyle}>Order ID</TableCell>
        <TableCell sx={headerStyle}>Name</TableCell>
        <TableCell sx={headerStyle}>Email</TableCell>
        <TableCell sx={headerStyle}>Payment Source</TableCell>
        <TableCell sx={headerStyle}>Details</TableCell>
        <TableCell sx={headerStyle}>Payment Status</TableCell>
        <TableCell sx={headerStyle}>Refund</TableCell>
      </TableRow>
    </TableHead>
  );
}

function formatMetaKey(key) {
  return key
    .replace(/^_+/, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function DetailRow({ label, value }) {
  return (
    <Box display="flex" justifyContent="space-between" sx={{ gap: 2 }}>
      <Typography sx={{ fontSize: "13px", color: "#3c434a", fontWeight: "bold" }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "13px", textAlign: "right", wordBreak: "break-all" }}>
        {value === "" || value === null || value === undefined ? "-" : String(value)}
      </Typography>
    </Box>
  );
}

function TransactionDetailsDialog({ row, open, onClose, isRefunded }) {
  const rows = [
    ["Order ID", `#${row.orderId}`],
    ["Name", row.name || "-"],
    ["Email", row.email || "-"],
    ["Payment Source", row.payment_method_title || row.payment_method || "-"],
    [
      "Payment Status",
      isRefunded ? "Refunded" : row.payment_status !== "unpaid" ? "Paid" : "Unpaid",
    ],
    ["Amount", row.total ? `${row.total} ${row.currency || ""}`.trim() : "-"],
    ["Transaction ID", row.transaction_id || "-"],
  ];

  const paymentMetaData = (row.meta_data || []).filter(
    (m) => m && m.key && m.key.toLowerCase() !== "pcrm_payment_status",
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Transaction Details</DialogTitle>
      <DialogContent dividers sx={{ maxHeight: "70vh" }}>
        <Stack spacing={1.5}>
          {rows.map(([label, value]) => (
            <DetailRow key={label} label={label} value={value} />
          ))}
        </Stack>

        {paymentMetaData.length > 0 && (
          <>
            <Typography
              sx={{ fontSize: "14px", fontWeight: "bold", mt: 3, mb: 1.5 }}
            >
              Payment Data
            </Typography>
            <Stack spacing={1.5}>
              {paymentMetaData.map((meta) => (
                <DetailRow
                  key={meta.id || meta.key}
                  label={formatMetaKey(meta.key)}
                  value={meta.value}
                />
              ))}
            </Stack>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function RefundDialog({ row, open, onClose, onRefunded }) {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setAmount(row.total || "");
      setReason("");
      setError("");
    }
  }, [open, row.total]);

  const handleConfirm = async () => {
    if (!amount || Number(amount) <= 0) {
      setError("Please enter a valid refund amount");
      return;
    }
    if (!reason.trim()) {
      setError("Please enter a reason for the refund");
      return;
    }

    setLoading(true);
    const userid = localStorage.getItem("auth_id");
    const username = localStorage.getItem("auth_name");
    const payload = {
      order_id: row.orderId,
      amount: Number(amount),
      message: reason,
      custom_order: row.pcrm_order_created_through === "arte",
      pcrm_refund_initiated_userid: userid,
      pcrm_refund_initiated_username: username,
    };

    try {
      const res = await refundOrderTransaction(payload);
      setLoading(false);
      if (res) {
        TrackEvents(`${row.orderId}`, {
          process: `Order Refunded From Transactions Page with amount ${amount} for reason: ${reason}`,
        });
        onRefunded(row.orderId);
        onClose();
      } else {
        setError("Opps something went wrong");
      }
    } catch (err) {
      setLoading(false);
      console.log(err);
      setError("Opps something went wrong");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Refund Order #{row.orderId}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <TextField
            label="Refund Amount"
            type="number"
            size="small"
            fullWidth
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {row.currency || "$"}
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="Reason for refund"
            size="small"
            fullWidth
            multiline
            minRows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          {error && (
            <Typography color="error" fontSize={13}>
              {error}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="error"
          sx={{ textTransform: "none" }}
          disabled={loading}
        >
          {loading ? <CircularProgress size={18} thickness={5} /> : "Confirm Refund"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function RefundCell({ row, onRefunded, isRefunded }) {
  const [open, setOpen] = useState(false);

  if (isRefunded) {
    return (
      <span style={{ color: "#d30000", fontWeight: "bold" }}>Refunded</span>
    );
  }

  if (row.payment_status !== "paid") {
    return <span>-</span>;
  }

  return (
    <>
      <Button
        variant="outlined"
        color="error"
        size="small"
        sx={{ textTransform: "none" }}
        onClick={() => setOpen(true)}
      >
        Refund
      </Button>
      {open && (
        <RefundDialog
          row={row}
          open={open}
          onClose={() => setOpen(false)}
          onRefunded={onRefunded}
        />
      )}
    </>
  );
}

export default function Transaction() {
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(20);
  const [orders, setOrders] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchvalue, setSearchValue] = useState("");
  const [search, setSearch] = useState("");
  const [openModel, setOpenModel] = useState(false);
  const [rowData, setRowData] = useState({});
  const [refundedIds, setRefundedIds] = useState([]);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        const data = await getTransactions(page + 1, rowsPerPage, search);
        setOrders(data?.responseData?.response || []);
        setCount(data?.responseData?.order_count?.count || 0);
        setLoading(false);
      } catch (error) {
        console.log(error);
        setLoading(false);
      }
    };
    fetchOrders();
  }, [page, search, rowsPerPage]);

  const handleOrderRefunded = (orderId) => {
    setRefundedIds((prev) => [...prev, orderId]);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleModelOpen = (row) => {
    setRowData(row);
    setOpenModel(true);
    TrackEvents(`${row.orderId}`, {
      process: "Open Transaction Details Modal",
    });
  };

  const handleCloseModel = () => {
    setOpenModel(false);
    setRowData({});
  };

  const handleSearch = (e) => {
    setSearchValue(e.target.value);
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setSearch(searchvalue);
    setPage(0);
  };

  return (
    <Layout>
      <Box
        bgcolor="#f0f0f1"
        minHeight="calc(100vh - 67px)"
        px={3}
        py={3}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography sx={{ fontSize: "23px", lineHeight: 1.3 }}>
            Transactions
          </Typography>
        </Box>
        <Stack direction="row" justifyContent="flex-end" mt={2}>
          <form onSubmit={handleSearchSubmit}>
            <TextField
              id="transaction-search"
              sx={{ m: 1, width: "30ch", backgroundColor: "#fff", fontSize: "14px" }}
              size="small"
              placeholder="Search Order"
              onChange={handleSearch}
              value={searchvalue}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon onClick={handleSearchSubmit} style={{ cursor: "pointer" }} />
                  </InputAdornment>
                ),
              }}
            />
          </form>
        </Stack>
        <Paper sx={{ width: "100%", mb: 2 }}>
          {loading && <LinearProgress />}
          <TableContainer>
            <Table size="small" aria-label="transactions table">
              <EnhancedTableHead />
              {orders && orders.length > 0 ? (
                <TableBody>
                  {orders.map((row) => {
                    const isRefunded =
                      row.refund === true ||
                      refundedIds.includes(row.orderId) ||
                      row.payment_status === "refunded";
                    return (
                      <TableRow hover key={row.orderId}>
                        <TableCell sx={{ whiteSpace: "nowrap", padding: "6px 8px" }}>
                          <Link
                            to={`/orders/1/details?id=${row.orderId}&tab=0`}
                            style={{ color: "#2271b1", textDecoration: "none", fontWeight: "bold" }}
                          >
                            #{row.orderId}
                          </Link>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap", padding: "6px 8px" }}>
                          {row.name || "-"}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap", padding: "6px 8px" }}>
                          {row.email || "-"}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap", padding: "6px 8px" }}>
                          {row.payment_method_title || row.payment_method || "-"}
                        </TableCell>
                        <TableCell sx={{ padding: "6px 8px" }}>
                          <IconButton
                            size="small"
                            onClick={() => handleModelOpen(row)}
                          >
                            <VisibilityOutlinedIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap", padding: "6px 8px" }}>
                          {isRefunded ? (
                            <span style={{ color: "#d30000", fontWeight: "bold" }}>
                              Refunded
                            </span>
                          ) : row.payment_status !== "unpaid" ? (
                            <span className="highlight-yes">Paid</span>
                          ) : (
                            <span style={{ color: "#d30000", fontWeight: "bold" }}>
                              Unpaid
                            </span>
                          )}
                        </TableCell>
                        <TableCell sx={{ padding: "6px 8px" }}>
                          <RefundCell
                            row={row}
                            onRefunded={handleOrderRefunded}
                            isRefunded={isRefunded}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              ) : (
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Box style={{ textAlign: "center" }}>
                        <Typography>
                          {loading ? "Loading..." : "No Transaction(s) Found"}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[20]}
            component="div"
            count={count}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
          />
        </Paper>
        {openModel && (
          <TransactionDetailsDialog
            row={rowData}
            open={openModel}
            onClose={handleCloseModel}
            isRefunded={
              rowData.refund === true ||
              refundedIds.includes(rowData.orderId) ||
              rowData.payment_status === "refunded"
            }
          />
        )}
      </Box>
    </Layout>
  );
}
